import { React, useState } from "react";
import axios from "axios";
import Loader from "../../Global/Loader";
import { useNavigate } from "react-router-dom";

function NewRequests({ consultations = [], consultants = [], fetchConsultants, fetchConsultations }) {
  const [selectedConsultantIds, setSelectedConsultantIds] = useState({}); // Track selected consultant for each row
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Handle consultant selection for a specific row
  const handleConsultantChange = (consultationId, consultantId) => {
    setSelectedConsultantIds((prev) => ({
      ...prev,
      [consultationId]: consultantId, // Update the selected consultant for the specific row
    }));
  };

  // Handle save for a specific row
  const handleSave = async (consultationId) => {
    const selectedConsultantId = selectedConsultantIds[consultationId];
    if (!selectedConsultantId) {
      alert("Please select a consultant first");
      return;
    }

    try {
      setLoading(true);
      if (selectedConsultantId === "admin") {
        await axios.patch(
          `${process.env.REACT_APP_BASE_URL}/consultation/${consultationId}/update`,
          {
            consultantName: "Admin",
            status: "In Process",
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        await axios.patch(
          `${process.env.REACT_APP_BASE_URL}/consultation/${consultationId}/update`,
          {
            consultantId: selectedConsultantId,
            status: "In Process",
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }
      fetchConsultants();
      fetchConsultations();
      setIsSubmitted(true);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      {isSubmitted && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Success!</h2>
            <h4 className="text-sm text-gray-600 mb-6">
              Your application has been submitted successfully.
            </h4>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setIsSubmitted(null)}
                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Something went wrong!</h2>
            <h4 className="text-sm text-gray-600 mb-6">
              We encountered an issue while processing your request. Please try again later. If the problem persists, contact support.
            </h4>
            <div className="flex justify-end space-x-4">
              <button className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none">
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap space-x-6">
        <div className="flex flex-col space-y-6 w-full">
          <div className="px-10 py-6 bg-white rounded ">
            <table className="w-full table-auto border-t border-b border-gray-300">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="px-4 py-2 text-center text-gray-400">Consultation ID</th>
                  <th className="px-4 py-2 text-center text-gray-400">Name</th>
                  <th className="px-4 py-2 text-center text-gray-400">Email</th>
                  <th className="px-4 py-2 text-center text-gray-400">Cell Phone</th>
                  <th className="px-4 py-2 text-center text-gray-400">Consultant</th>
                  <th className="px-4 py-2 text-center text-gray-400">Action</th>
                </tr>
              </thead>
              <tbody>
                {consultations.length > 0 ? (
                  consultations.map((consultation) => (
                    <tr key={consultation._id} className="border-b border-gray-300 cursor-pointer text-center">
                      <td className="px-4 py-4 text-sm text-gray-700">{consultation.consultationId}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">{consultation.studentName}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">{consultation.email}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">{consultation.cellPhone}</td>
                      <td className="px-4 py-4 text-sm font-thin">
                        <select
                          onChange={(e) => handleConsultantChange(consultation._id, e.target.value)}
                          className="w-full px-3 py-2 text-sm font-thin text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 cursor-pointer hover:border-blue-400"
                          value={selectedConsultantIds[consultation._id] || ""}
                        >
                          <option value="" hidden>
                            {consultation.consultantName || "Select Consultant"}
                          </option>
                          <option value="admin" className="font-medium text-blue-600">
                            Admin
                          </option>
                          {consultants.map((consultant) => (
                            <option key={consultant._id} value={consultant._id}>
                              {consultant.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-4 text-sm font-thin flex justify-center space-x-2">
                        <button
                          onClick={() => handleSave(consultation._id)}
                          disabled={!selectedConsultantIds[consultation._id]} // Disable if no consultant is selected
                          className="p-2 text-green-600 hover:bg-green-50 rounded-full transition-colors disabled:opacity-50"
                          title="Save"
                        >
                          <i className="fas fa-save"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-4 text-sm text-gray-700 text-center">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewRequests;